/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import template from './tag-based-related-content-slider.stache';
import can from 'can';
import DomEventsManager from 'scripts/dom-events-manager';
import Swiper from 'swiper';
import Map from 'scripts/models/base/map';
import dom from 'scripts/utils/dom';
import dtm from 'scripts/services/dtm';
import FeedDataService from 'scripts/services/timeline-feed-service';
import { clickHandler } from 'components/timeline/card/card.controller';

const encodeCmsTags = (decoded) => decoded.replace(/[:/\s]+/g, '_');

const cmsTags = dom.getCmsTags();
const encodedTags = encodeCmsTags(cmsTags);

const Model = can.Map.extend({
    initLoad: true,
    define: {
        slides: {
            Type: Map.List,
            Value: '*',
        },
        currentPage: {
            type: 'number',
            value: 1,
        },
        totalNumberOfPages: {
            type: 'number',
            value: 1,
        },
    },
    onClick() {
        const card = this.attr('card');
        clickHandler(card && card.serialize());
    },
});

Component.extend({
    tag: 'tag-based-related-content-slider',
    template: template,
    leakScope: false,
    viewModel: Model,
    events: {
        init: function() {
            DomEventsManager.subscribe(this);
            this.dataContainer = this.element.find(
                '.s-tag-based-related-content-slider__container',
            );

            this.timelineLoader = new FeedDataService()
                .on('data', (ev, data, total, index) => {
                    if (data && data.length > 0) {
                        this.viewModel.attr('totalNumberOfPages', total);

                        data.sort((d1, d2) => {
                            return d2.date - d1.date;
                        });

                        if (this.viewModel.attr('initLoad')) {
                            this.viewModel.attr('initLoad', false);
                            this.data = [...data];

                            this.viewModel.attr('slides', data);

                            setTimeout(() => {
                                this.initSwiper();
                            }, 0);
                        } else {
                            if (data && data.length > 0) {
                                this.viewModel.slides.push(...data);
                                this.swiper.update();
                            }
                        }
                    }
                })
                .on('error', (error) => {
                    console.error('Error fetching content feed data', error);
                });

            this.loadCards();
        },

        removed: function() {
            DomEventsManager.unsubscribe(this);
        },

        loadCards() {
            const type = this.viewModel.attr('type');
            const page = this.viewModel.attr('currentPage');
            const tags = this.getEncodedTags();
            this.timelineLoader.loadLatest(type, page, tags);
            this.viewModel.attr(
                'currentPage',
                this.viewModel.attr('currentPage') + 1,
            );
        },

        initSwiper() {
            const documentBody = document.getElementsByTagName('body')[0];
            const triggerFocus = new Event('slidetransitionend');
            const hideFocusHiddenSlides = () => {
                swiperContainer
                    .find('.s-tag-based-related-content-slider-item')
                    .find('a')
                    .attr('aria-hidden', true)
                    .attr('tabindex', -1);
                swiperContainer
                    .find('.s-tag-based-related-content-slider-item')
                    .find('button')
                    .attr('aria-hidden', true)
                    .attr('tabindex', -1);
                swiperContainer
                    .find(
                        '.s-tag-based-related-content-slider-item social-share',
                    )
                    .attr('aria-hidden', true);
                swiperContainer
                    .find('.swiper-slide-visible')
                    .find('a')
                    .attr('aria-hidden', false)
                    .not('a.c-social-share-icon')
                    .attr('tabindex', 0);
                swiperContainer
                    .find('.swiper-slide-visible')
                    .find('button')
                    .attr('aria-hidden', false)
                    .attr('tabindex', 0);
                swiperContainer
                    .find('.swiper-slide-visible social-share')
                    .attr('aria-hidden', false);
                documentBody.dispatchEvent(triggerFocus);
            };

            const swiperContainer = this.element.find(
                '.tag-based-content-swiper-container',
            );
            this.swiper = new Swiper(swiperContainer, {
                centeredSlides: false,
                spaceBetween: 30,
                slidesPerView: 'auto',
                slidesPerGroup: 2,
                touchRatio: 0.2,
                freeMode: true,
                slideToClickedSlide: false,
                wrapperClass: 's-tag-based-related-content-slider__container',
                slideClass: 's-tag-based-related-content-slider-item',
                watchSlidesVisibility: true,
                observer: true,
                observeParents: true,
                navigation: {
                    nextEl: swiperContainer.find(
                        '.s-tag-based-related-content-slider-button--next',
                    ),
                    prevEl: swiperContainer.find(
                        '.s-tag-based-related-content-slider-button--prev',
                    ),
                },
                on: {
                    init: () => {
                        setTimeout(() => {
                            hideFocusHiddenSlides();
                        }, 1000);
                    },
                    reachEnd: () => {
                        setTimeout(() => {
                            if (
                                !swiperContainer
                                    .find(
                                        '.s-tag-based-related-content-slider-button--next',
                                    )
                                    .is(':visible')
                            ) {
                                swiperContainer
                                    .find(
                                        '.s-tag-based-related-content-slider-button--prev',
                                    )
                                    .focus();
                            }
                            hideFocusHiddenSlides();
                            documentBody.dispatchEvent(triggerFocus);
                        }, 200);
                    },
                },
            });

            this.swiper.on('slideChangeTransitionStart', () =>
                this.appendMoreSlides(),
            );

            this.swiper.on('slideChangeTransitionEnd', () => {
                hideFocusHiddenSlides();
            });
        },

        getEncodedTags() {
            const tags = this.viewModel.attr('tags');
            if (tags && tags.length > 0) {
                return tags.map((tag) => tag.key).join(',');
            }
            return encodedTags;
        },

        getModuleName() {
            switch (this.viewModel.attr('type')) {
                case 'photos':
                    return 'Photo Gallery';
                case 'articles':
                    return 'Article';
                case 'videos':
                    return 'Video Player';
            }
            return '';
        },

        getStringifiedTags(tags) {
            if (tags && tags.length > 0) {
                return tags
                    .map((item) => {
                        return item.key;
                    })
                    .join(',');
            }
            return '';
        },

        appendMoreSlides() {
            let invisibleToRight = this.element
                .find('.s-tag-based-related-content-slider-item')
                .filter((index, slide) => {
                    return $(slide).position().left > $(window).width();
                });
            if (
                invisibleToRight.length <= 2 &&
                this.viewModel.attr('currentPage') <
                    this.viewModel.attr('totalNumberOfPages')
            ) {
                this.loadCards();
            }
        },

        '.s-tag-based-related-content-slider-item click'(element) {
            const cardIndex = $(element).index();
            const cards = this.viewModel.attr('slides');

            if (cards.length > cardIndex) {
                const selectedCard = cards[cardIndex];
                dtm.trackRelatedContentCardClick(
                    this.getModuleName(),
                    this.getStringifiedTags(selectedCard.tags),
                );
            }
        },
    },
});
